/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import 'lazysizes';
import { gsap, ScrollTrigger } from 'gsap/all';
import { debounce } from 'lodash';
// import Rellax from 'rellax';
import Vue from 'vue';
import Headroom from 'headroom.js';
import zenScroll from 'zenscroll';
// import Router from './router';

// gsap.registerPlugin(CustomEase);
gsap.registerPlugin(ScrollTrigger);

require('./bootstrap');
require('./components/ask-the-team-component');
require('./components/cookie-component');
require('./components/cookie-declaration-component');
require('./components/custom-solutions-component');
require('./components/download-document-component');
require('./components/dropdown-component');
require('./components/file-upload-component');
require('./components/filter-news-component');
require('./components/home-hero-slider-component');
require('./components/job-application-component');
require('./components/lets-meet-component');
require('./components/list-scroll-component');
require('./components/logo-ticker-component');
require('./components/newsletter-component');
require('./components/popup-component');
require('./components/request-quote-component');
require('./components/slider-component');
require('./components/load-filter-component');
require('./components/testimonial-slider');
require('./components/support-component');
require('./components/testimonial-clients');
require('./components/instagram-component');
require('./components/video-component');
require('./components/faq-component');

const App = new Vue({
    el: '#app',
    delimiters: ['${', '}'],

    data() {
        return {
            // router: {
            //     currentSection: null,
            //     instance: null,
            //     prefetch: true,
            // },
            showNavigation: false,
            header: null,
            headroom: null,
            activeSubNav: null,
            defaultActiveNavItem: null,
            activeNavItem: null,
            previousWindowWidth: window.innerWidth,
            scriptsToLoad: [],
        };
    },

    watch: {
        isMobile(value) {
            EventBus.$emit('is-mobile-changed', value);
        },

        isTablet(value) {
            EventBus.$emit('is-tablet-changed', value);
        },

        showNavigation(visible) {
            EventBus.$emit('navigation-state-changed', visible);

            if (visible) {
                document.body.classList.add('body-lock');
                return;
            }

            document.body.classList.remove('body-lock');
        },
    },

    mounted() {
        this.defaultActiveNavItem = document.querySelector('[data-active-nav-uid]').dataset.activeNavUid;
        this.activeNavItem = this.defaultActiveNavItem;
        this.header = document.querySelector('.header-holder');
        this.headroom = new Headroom(this.header, {
            onUnpin: () => {
                this.unpinHeadroom();
            },
        });
        this.headroom.init();

        this.initRevealTriggers();

        // Window events
        window.addEventListener('resize', debounce(this.onWindowResize, 150));
        document.addEventListener('readystatechange', () => {
            EventBus.$emit('document-ready-state', document.readyState);
        });

        EventBus.$on('document-ready-state', (state) => {
            if (state === 'complete') {
                const sharpSpringEls = document.querySelectorAll('[id^="ss-"]');
                // Clone so this has no direct relation with the DOM
                this.scriptsToLoad = [...sharpSpringEls];

                this.loadSharpSpring();
            }
        });
    },

    methods: {
        afterLoadAll() {
            // Handle anchor linking
            if (window.location.hash) {
                const element = document.querySelector(window.location.hash);
                if (element) {
                    zenScroll.to(element);
                }
            }
        },

        loadSharpSpring() {
            // If no script is left to load, we consider this completed and call the afterLoadAll method OR there are no scripts to load
            if (this.scriptsToLoad.length === 0) {
                setTimeout(() => {
                    this.afterLoadAll();
                }, 250);
                return;
            }

            const previousScript = document.getElementById('default-ss-loader');

            if (previousScript) {
                previousScript.remove();
            }

            const { ssRef } = this.scriptsToLoad[0].dataset;
            const ssClass = this.scriptsToLoad[0].getAttribute('id');

            const formSetup = document.createElement('script');
            formSetup.setAttribute('type', 'application/javascript');
            formSetup.innerHTML = `
                var ss_form = { 'account': 'MzawMLE0MzczBgA', 'formID': '${ssRef}' };
                ss_form.width = '100%';
                ss_form.domain = 'app-3QNUW33EFM.marketingautomation.services';
                ss_form.hidden = {'_usePlaceholders': true};
                ss_form.target_id = '${ssClass}'; // Optional parameter: forms will be placed inside the element with the specified id
            `;

            document.body.appendChild(formSetup);

            const tag = document.createElement('script');
            tag.setAttribute('id', 'default-ss-loader');
            tag.setAttribute('type', 'application/javascript');
            tag.setAttribute(
                'src',
                'https://koi-3QNUW33EFM.marketingautomation.services/client/form.js?ver=2.0.1',
            );

            document.body.appendChild(tag);

            this.scriptsToLoad.shift();

            setTimeout(() => {
                this.loadSharpSpring();
            }, 1000);
        },

        toggleFooterList(event) {
            const footerBlock = event.currentTarget.parentElement;
            const footerList = event.currentTarget.nextElementSibling;

            if (footerBlock.classList.contains('active')) {
                footerBlock.classList.remove('active');
                gsap.to(footerList, 0.3, {
                    height: 0,
                    ease: 'Quad.easeOut',
                });
            } else {
                footerBlock.classList.add('active');
                gsap.to(footerList, 0.3, {
                    height: 'auto',
                    ease: 'power4.out',
                });
            }
        },

        mouseOverMenu(e, id, uid) {
            if (!window.matchMedia('(min-width: 1024px)').matches) {
                return;
            }

            this.toggleSubNav(e, id, uid);
        },

        closeSubMenu() {
            if (!this.activeSubNav) {
                return;
            }

            this.closeMenu();
            // this.activeNavItem = null;
            // this.activeSubNav = null;
        },

        toggleSubNav(e, id, uid = null) {
            // Check if mobile, if mobile handle mobile menu
            if (!window.matchMedia('(min-width: 1024px)').matches) {
                this.handleMobileMenu(id);
                return;
            }

            // Close menu
            if (this.activeSubNav === id) {
                this.closeMenu();
                return;
            }

            // Return if e is not defined
            if (!e) {
                return;
            }

            // Get subList if available (not available when unit menu is triggered)
            const subList = e.currentTarget.parentElement.querySelector('.navigation__direct-secondary-list');

            // Reset values
            gsap.set(['.navigation__secondary-list', '.navigation__coverage-holder'], {
                y: 0,
                autoAlpha: 1,
            });

            // Toggle between different menu dropdown
            if (this.activeSubNav) {
                this.openOtherMenu(id, uid, subList);
                return;
            }

            // Open menu from a closed one
            this.openMenu(id, uid, subList);
        },

        handleMobileMenu(id) {
            if (this.activeSubNav === id) {
                this.activeSubNav = null;
                return;
            }

            this.activeSubNav = id;
        },

        openMenu(id, uid, subList = null) {
            this.activeSubNav = id;
            this.activeNavItem = uid;

            Vue.nextTick(() => {
                if (subList) {
                    this.listHeight = subList.getBoundingClientRect().height + 50 + 20;
                } else {
                    // Unit menu
                    this.listHeight = document.querySelector('.navigation__secondary-holder-active').getBoundingClientRect().height + 120 + 20;
                }

                const directSecondaryItems = subList ? subList.querySelectorAll('.navigation__direct-secondary-item-holder') : null;

                const openSubMenuTL = gsap.timeline();

                openSubMenuTL.to('.header-background', { duration: 0.8, y: this.listHeight, ease: 'power4.out' });
                openSubMenuTL.fromTo(
                    ['.navigation__secondary-list', '.navigation__coverage-holder', directSecondaryItems],
                    {
                        y: -20,
                        autoAlpha: 0,
                    },

                    {
                        duration: 0.8, autoAlpha: 1, y: 0, stagger: 0.1, ease: 'power4.out',
                    },
                    0.3,
                );

                openSubMenuTL.fromTo('.navigation__search-holder', {
                    autoAlpha: 0,
                }, {
                    autoAlpha: 1,
                    ease: 'power4.out',
                }, '<');

                openSubMenuTL.play();
            });
        },

        openOtherMenu(id, uid, subList = null) {
            this.activeNavItem = uid;

            gsap.to(['.navigation__secondary-list', '.navigation__coverage-holder'], {
                duration: 0.8, autoAlpha: 0, y: -20, ease: 'power4.out',
            });

            gsap.delayedCall(0.3, () => {
                this.activeSubNav = id;

                Vue.nextTick(() => {
                    if (subList) {
                        this.listHeight = subList.getBoundingClientRect().height + 50 + 20;
                    } else {
                        this.listHeight = document.querySelector('.navigation__secondary-holder-active').getBoundingClientRect().height + 120 + 20;
                    }

                    const openSubMenuTL = gsap.timeline();

                    const directSecondaryItems = subList ? subList.querySelectorAll('.navigation__direct-secondary-item-holder') : null;

                    openSubMenuTL.to('.header-background', { duration: 0.8, y: this.listHeight, ease: 'power4.out' });
                    openSubMenuTL.fromTo(
                        ['.navigation__secondary-list', '.navigation__coverage-holder', directSecondaryItems],
                        {
                            y: -20,
                            autoAlpha: 0,
                        },
                        {
                            duration: 0.8, autoAlpha: 1, y: 0, stagger: 0.1, ease: 'power4.out',
                        },
                        0,
                    );

                    openSubMenuTL.fromTo('.navigation__search-holder', {
                        autoAlpha: 0,
                    }, {
                        autoAlpha: 1,
                        ease: 'power4.out',
                    }, '<');

                    openSubMenuTL.play();
                });
            });
        },

        closeMenu() {
            this.activeNavItem = null;

            this.$nextTick(() => {
                this.activeNavItem = this.defaultActiveNavItem;
            });
            let closeSubMenuTL = null;

            if (!closeSubMenuTL) {
                closeSubMenuTL = gsap.timeline({
                    paused: true,
                    onComplete: () => {
                        this.activeSubNav = null;
                    },
                });

                closeSubMenuTL.to(['.navigation__secondary-list', '.navigation__coverage-holder'], {
                    duration: 0.8, autoAlpha: 0, y: -20, ease: 'power4.out',
                });
                closeSubMenuTL.to('.navigation__search-holder', { duration: 0.8, autoAlpha: 0, ease: 'power4.out' }, '<');
                closeSubMenuTL.to('.header-background', { duration: 0.8, y: 0, ease: 'power4.out' }, 0.3);
            }

            closeSubMenuTL.play();
        },

        unpinHeadroom() {
            if (!window.matchMedia('(min-width: 1024px)').matches) {
                return;
            }

            if (!this.activeSubNav) {
                return;
            }

            const closeSubMenuTL = gsap.timeline({

                onComplete: () => {
                    this.activeSubNav = null;
                },
            });

            closeSubMenuTL.to(['.navigation__secondary-list', '.navigation__coverage-holder'], {
                duration: 0.8, autoAlpha: 0, y: -20, ease: 'power4.out',
            });
            closeSubMenuTL.to('.header-background', { duration: 0.8, y: 0, ease: 'power4.out' }, 0.3);
        },

        initRevealTriggers() {
            const elements = [
                ...document.querySelectorAll('.a-fade-up'),
                ...document.querySelectorAll('.a-fade-in'),
                ...document.querySelectorAll('[data-reveal]'),
                ...document.querySelectorAll('[data-image-reveal]'),
                ...document.querySelectorAll('[data-video-reveal]'),
            ];

            const countEl = [
                ...document.querySelectorAll('[data-count-reveal]'),
            ];

            if (countEl) {
                this.initCountRevealTriggers(countEl);
            }

            elements.forEach((element) => {
                const { revealDelay, revealManually, revealPosition } = element.dataset;
                if (revealManually === '') {
                    return;
                }

                if (revealPosition) {
                    // console.log(revealPosition);
                }

                const startPercentage = revealPosition || '90%';

                ScrollTrigger.create({
                    trigger: element,
                    start: `top ${startPercentage}`,
                    onEnter: () => {
                        if (!revealDelay) {
                            element.classList.add('--revealed');
                            return;
                        }

                        gsap.delayedCall(revealDelay, () => {
                            element.classList.add('--revealed');
                        });
                    },
                });
            });
        },

        initCountRevealTriggers(el) {
            el.forEach((element) => {
                if (element.innerText.indexOf('.') > -1) {
                    const fixedTo = element.dataset.number.split('.').pop().length;

                    const count = element;
                    const zero = { val: 0 };

                    gsap.to(zero, {
                        val: count.dataset.number,
                        duration: 4,
                        ease: 'power1.easeOut',
                        modifiers: {
                            val: (val) => val.toFixed(fixedTo),
                        },
                        onUpdate() {
                            count.innerText = zero.val;
                        },
                    });
                    return;
                }

                gsap.from(element, {
                    textContent: 0,
                    duration: 4,
                    ease: 'power1.easeOut',
                    snap: { textContent: 1 },
                });
            });
        },

        /**
 * Raised when the client window is resizing.
 *
 * @return {Void}
 */
        onWindowResize() {
            if (this.previousWindowWidth === window.innerWidth) {
                return;
            }

            this.previousWindowWidth = window.innerWidth;
            EventBus.$emit('window-resize');
        },

        detectMobile() {
            this.isMobile = window.innerWidth <= 768;
            this.isTablet = window.innerWidth <= 1024;
        },
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.onWindowResize);
    },
});
